import React from 'react';
import { Button, Form, Input, InputRef, Modal, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

import Particles from "react-tsparticles";
import type { Engine, RecursivePartial, IOptions } from "tsparticles-engine";
import { loadFull } from "tsparticles";

import { motion, useIsPresent } from 'framer-motion';

import useAuth from '../../hooks/useAuth';
import useNoti from '../../hooks/useNoti';
import { LoginPayload } from '../../api/auth';

const { Title, Text } = Typography;

const particleOptions: RecursivePartial<IOptions> = {
    background: {
        color: {
            value: "rgb(26, 37, 47)",
        },
    },
    fpsLimit: 60,
    interactivity: {
        events: {
            onClick: {
                enable: false,
                mode: "push",
            },
            onHover: {
                enable: false,
                mode: "repulse",
            },
            resize: true,
        },
        modes: {
            push: {
                quantity: 4,
            },
            repulse: {
                distance: 200,
                duration: 0.4,
            },
        },
    },
    particles: {
        color: { value: '#FF8300' },
        links: {
            color: "#FF8300",
            distance: 150,
            enable: true,
            opacity: 0.5,
            width: 1,
        },
        collisions: {
            enable: false,
        },
        move: {
            direction: "none",
            enable: true,
            outModes: {
                default: "out",
            },
            random: true,
            speed: 3,
            straight: false,
        },
        number: {
            density: {
                enable: true,
                area: 800,
            },
            value: 80,
        },
        opacity: {
            value: 0.5,
        },
        shape: {
            type: "circle",
        },
        size: {
            value: { min: 1, max: 5 },
        },
    }
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const isPresent = useIsPresent();

    const noti = useNoti();
    const otpRef = React.useRef<InputRef>(null);

    const [isLoading, setIsLoading] = React.useState<boolean | undefined>(false);
    const [otpUser, setOtpUser] = React.useState<{
        email: string;
        password: string;
    } | undefined>();

    const [confirmOtpLoading, setComfirmOtpLoading] = React.useState(false);
    const [openOtpModal, setOpenOtpModal] = React.useState(false);


    const particlesInit = React.useCallback(async (engine: Engine) => {
        //console.log(engine);

        // you can initialize the tsParticles instance (engine) here, adding custom shapes or presets
        // this loads the tsparticles package bundle, it's the easiest method for getting everything ready
        // starting from v2 you can add only the features you need reducing the bundle size
        await loadFull(engine);
    }, []);

    if (auth && auth?.hasToken) {
        return <Navigate to="/dashboard" state={{ from: location }} />
        //navigate('/dashboard', { replace: true })
    }

    const onFinish = async (values: LoginPayload) => {
        setIsLoading(true);
        try {
            await auth?.signin(
                {
                    email: values.email,
                    password: values.password
                },
                () => {
                    return navigate('/dashboard', { replace: false });
                }
            );
        } catch (err: any) {
            setIsLoading(false);
            const {
                name,
                message: { data: { detail }, status, statusText }
            } = err;
            if (name === 'AxiosError' && status === 400 && detail.indexOf("OTP") > 0) {
                // Display OTP Form to perform login.  
                setOtpUser({
                    email: values.email,
                    password: values.password
                });
                setOpenOtpModal(true);
            }
            else {
                noti?.pushNoti({
                    type: 'error',
                    message: statusText,
                    description: detail,
                    duration: 7
                });
            }
        }
    };

    const handleConfirmOtp = async () => {
        //console.log(`${typeof otpRef.current?.input?.value}`, otpRef.current?.input?.value);
        // call login api again with otp
        try {
            setComfirmOtpLoading(true);
            if (otpUser && otpUser.email && otpUser.password) {
                await auth?.signin(
                    {
                        email: otpUser.email,
                        password: otpUser.password,
                        otp: otpRef.current?.input?.value
                    },
                    () => {
                        return navigate('/dashboard', { replace: false });
                    }
                );
            }

        } catch (err: any) {
            setComfirmOtpLoading(false);
            const {
                message: { data: { detail }, statusText }
            } = err;
            noti?.pushNoti({
                type: 'error',
                message: statusText,
                description: detail,
                duration: 7
            });
        }
    }

    const handleCancelOtp = () => setOpenOtpModal(false);


    return (
        <>
            <Particles
                id='tsparticles'
                init={particlesInit}
                options={particleOptions}
            />
            <Modal
                open={openOtpModal}
                onOk={handleConfirmOtp}
                confirmLoading={confirmOtpLoading}
                onCancel={handleCancelOtp}
            >
                <Title>Two-factor authentication</Title>
                <Text>Enter the one-time password generated by your authenticator app</Text>
                <Input
                    ref={otpRef}
                    name='otp'
                    placeholder="One-time password"
                />
            </Modal>
            <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50 %, -50 %)',
                "WebkitTransform": 'translate(-50%, -50%)',
                "msTransform": 'translate(-50%, -50%)',
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 50,
                    maxWidth: 500,
                }}>

                    <div>
                        {/* Remove Logo */}
                    </div>
                    <div style={{
                        width: 300,
                        height: '100%',
                    }}>
                        <Form
                            name="login_form"
                            className="login-form"
                            onFinish={onFinish}
                        >
                            <Form.Item
                                name="email"
                                rules={[{ type: 'email', required: true, message: 'Please input your Email' }]}
                            >
                                <Input
                                    prefix={<UserOutlined className="site-form-item-icon" />}
                                    type="email"
                                    placeholder="Email"
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: 'Please input your Password!' }]}
                            >
                                <Input
                                    prefix={<LockOutlined className="site-form-item-icon" />}
                                    type="password"
                                    placeholder="Password"
                                />
                            </Form.Item>

                            {/* <Form.Item> */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <Button loading={isLoading} type="primary" htmlType="submit" className="login-form-button">
                                    Log in
                                </Button>
                                <span style={{ display: 'block', color: '#FF8300' }}>Or </span>
                                <Button
                                    type="link"
                                    htmlType="button"
                                    // onClick={() => window.open("https://portal.arubainstanton.com/", "_blank")}
                                    onClick={()=>navigate('/sign-up', { replace: false })}
                                    style={{ color: '#FF8300' }}
                                >
                                    Register →
                                </Button>                                
                                {/* <a href="https://portal.arubainstanton.com/">Go to Aruba portal</a> */}
                            </div>
                            {/* </Form.Item> */}
                        </Form>
                    </div>

                </div>
            </div>
            <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0, transition: { duration: 0.5, ease: "circOut" } }}
                exit={{ scaleX: 1, transition: { duration: 0.5, ease: "circIn" } }}
                style={{ originX: isPresent ? 0 : 1 }}
                className="privacy-screen"
            />
        </>
    )
}

export default LoginPage;